import { authManager } from './AuthManager';

/**
 * API interceptor that automatically adds authentication headers to requests
 */
export class ApiInterceptor {
  private originalFetch: typeof fetch;

  constructor() {
    // Properly bind the original fetch to maintain correct 'this' context
    this.originalFetch = window.fetch.bind(window);
    this.setupInterceptor();
  }

  private setupInterceptor(): void {
    // Override the global fetch function
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      return this.interceptRequest(input, init);
    };
  }

  private async interceptRequest(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // Clone the init object to avoid mutating the original
    const requestInit = { ...init };
    
    // Only add auth headers if authentication is enabled
    if (authManager.isAuthenticationEnabled()) {
      const accessToken = await authManager.getAccessToken();
      
      if (accessToken) {
        // Initialize headers if they don't exist
        if (!requestInit.headers) {
          requestInit.headers = {};
        }

        // Add authorization header
        if (requestInit.headers instanceof Headers) {
          requestInit.headers.set('Authorization', `Bearer ${accessToken}`);
        } else if (Array.isArray(requestInit.headers)) {
          // Handle array format
          const authHeaderIndex = requestInit.headers.findIndex(([key]) => 
            key.toLowerCase() === 'authorization'
          );
          
          if (authHeaderIndex >= 0) {
            requestInit.headers[authHeaderIndex] = ['Authorization', `Bearer ${accessToken}`];
          } else {
            requestInit.headers.push(['Authorization', `Bearer ${accessToken}`]);
          }
        } else {
          // Handle object format (most common)
          (requestInit.headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
        }
      }
    }

    // Make the actual request
    try {
      const response = await this.originalFetch(input, requestInit);
      
      // Handle authentication errors
      if (response.status === 401) {
        // Token might be expired or invalid
        logger.warn('Received 401 response, token might be expired');
        
        // Try to refresh token and retry the request once
        if (authManager.isAuthenticationEnabled()) {
          try {
            const newToken = await authManager.getAccessToken();
            if (newToken && requestInit.headers) {
              // Update the authorization header with the new token
              if (requestInit.headers instanceof Headers) {
                requestInit.headers.set('Authorization', `Bearer ${newToken}`);
              } else if (Array.isArray(requestInit.headers)) {
                const authHeaderIndex = requestInit.headers.findIndex(([key]) => 
                  key.toLowerCase() === 'authorization'
                );
                if (authHeaderIndex >= 0) {
                  requestInit.headers[authHeaderIndex] = ['Authorization', `Bearer ${newToken}`];
                }
              } else {
                (requestInit.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
              }
              
              // Retry the request with the new token
              return this.originalFetch(input, requestInit);
            }
          } catch (refreshError) {
            logger.error('Failed to refresh token for retry:', refreshError);
            // If refresh fails, let the original 401 response pass through
          }
        }
      }

      return response;
    } catch (error) {
      // Network or other errors
      logger.error('Request failed:', error);
      throw error;
    }
  }

  /**
   * Get the original fetch function (bypasses auth interceptor)
   */
  getOriginalFetch(): typeof fetch {
    return this.originalFetch;
  }

  /**
   * Restore the original fetch function (for testing or cleanup)
   */
  restore(): void {
    window.fetch = this.originalFetch;
  }
}

// Create and setup the interceptor
export const apiInterceptor = new ApiInterceptor();

/**
 * Helper function to make authenticated requests with automatic retry
 */
export async function authenticatedFetch(
  input: RequestInfo | URL, 
  init?: RequestInit,
  maxRetries: number = 1
): Promise<Response> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(input, init);
      
      if (response.status === 401 && attempt < maxRetries) {
        // Try to refresh token for next attempt
        try {
          await authManager.getAccessToken();
          continue; // Retry the request
        } catch (refreshError) {
          logger.error('Token refresh failed:', refreshError);
          break; // Stop retrying if refresh fails
        }
      }
      
      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Request failed');
      
      if (attempt < maxRetries) {
        // Wait a bit before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        continue;
      }
      
      break;
    }
  }
  
  throw lastError || new Error('Request failed after retries');
}

/**
 * Helper function to safely access environment variables in Vite
 */
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  try {
    // For Vite, environment variables are available in import.meta.env
    if (import.meta?.env?.[key]) {
      const envValue = import.meta.env[key];
      return String(envValue);
    }
    
    return defaultValue;
  } catch (error) {
    logger.warn(`Error accessing environment variable ${key}:`, error);
    return defaultValue;
  }
};

/**
 * Helper function to check if a request URL is a backend API call
 */
export function isApiRequest(input: RequestInfo | URL): boolean {
  const url = input instanceof URL ? input.href : 
              typeof input === 'string' ? input : 
              input.url;
  
  // Add your backend API URL patterns here
  const apiPatterns = [
    '/api/',
    '/backend/',
    'localhost:8080',
    getEnvVar('REACT_APP_API_BASE_URL'),
    getEnvVar('VITE_API_BASE_URL'),
  ].filter(Boolean);
  
  return apiPatterns.some(pattern => url.includes(pattern as string));
} 